import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowUpRight, ArrowDownLeft, Plus, Clock, ArrowLeft, CreditCard, CheckCircle, XCircle, Music, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { API_BASE as API } from '../lib/apiBase';


export default function WalletPage() {
  const { user, token } = useAuth();
  const { t, lang } = useLang();
  const [searchParams] = useSearchParams();
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDeposit, setShowDeposit] = useState(false);
  const [showWithdraw, setShowWithdraw] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [processing, setProcessing] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('info');
  const [depositPackages, setDepositPackages] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [pollingStatus, setPollingStatus] = useState(null);
  const [rates, setRates] = useState(null);

  const fetchData = async () => {
    if (!token) { setLoading(false); return; }
    try {
      const [w, tx, pkgs, ratesRes] = await Promise.all([
        fetch(`${API}/api/wallets/me`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${API}/api/wallets/transactions`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()),
        fetch(`${API}/api/economy/deposit-packages`).then(r => r.json()).catch(() => ({ packages: [] })),
        fetch(`${API}/api/economy/rates`).then(r => r.json()).catch(() => null),
      ]);
      setWallet(w);
      setTransactions(tx.transactions || []);
      setDepositPackages(pkgs.packages || []);
      if (ratesRes) setRates(ratesRes.rates);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [token]);

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const cancelled = searchParams.get('cancelled');
    if (cancelled) {
      setMessage(lang === 'es' ? 'Recarga cancelada' : 'Top up cancelled');
      setMessageType('warning');
      return;
    }
    if (sessionId && token) {
      pollPaymentStatus(sessionId, 0);
    }
  }, [searchParams, token]);

  const pollPaymentStatus = async (sessionId, attempts) => {
    if (attempts >= 8) {
      setPollingStatus(null);
      setMessage(lang === 'es' ? 'No pudimos verificar la recarga. Si fue exitosa, tu saldo se actualizara pronto.' : 'Could not verify top up.');
      setMessageType('warning');
      return;
    }
    setPollingStatus('checking');
    try {
      const res = await fetch(`${API}/api/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.payment_status === 'paid') {
        setPollingStatus('success');
        const corcheas = data.corcheas_credited || 0;
        setMessage(lang === 'es'
          ? `Recarga exitosa! +${corcheas} corcheas (${data.amount_eur}EUR)`
          : `Top up successful! +${corcheas} corcheas (${data.amount_eur}EUR)`);
        setMessageType('success');
        fetchData();
        return;
      }
      if (data.status === 'expired') {
        setPollingStatus(null);
        setMessage(lang === 'es' ? 'La sesion de recarga ha expirado.' : 'Top up session expired.');
        setMessageType('error');
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2500);
    } catch (e) {
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 3000);
    }
  };

  const simulatedDeposit = async (pkg) => {
    setProcessing(true);
    try {
      const res = await fetch(`${API}/api/wallets/deposit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ amount: pkg.amount_fiat, currency: pkg.currency || 'EUR' })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setMessage(lang === 'es'
          ? `+${data.corcheas_added} corcheas recargadas (${data.amount_fiat}EUR)`
          : `+${data.corcheas_added} corcheas topped up (${data.amount_fiat}EUR)`);
        setMessageType('success');
        setShowDeposit(false);
        setSelectedPackage(null);
        fetchData();
      } else {
        setMessage(data.detail || 'Error');
        setMessageType('error');
      }
    } catch (e) {
      setMessage('Error de conexion');
      setMessageType('error');
    }
    setProcessing(false);
  };

  const initiateStripeDeposit = async () => {
    if (!selectedPackage) return;
    setProcessing(true);
    try {
      const originUrl = window.location.origin;
      const res = await fetch(`${API}/api/payments/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ package_id: selectedPackage, origin_url: originUrl })
      });
      const data = await res.json();
      if (res.ok && data.url) {
        window.location.href = data.url;
      } else {
        // Stripe not configured — fall back to simulated deposit
        const pkg = depositPackages.find(p => p.id === selectedPackage);
        if (pkg) {
          await simulatedDeposit(pkg);
        } else {
          setMessage(data.detail || 'Error');
          setMessageType('error');
        }
      }
    } catch (e) {
      setMessage('Error de conexion');
      setMessageType('error');
    }
    setProcessing(false);
  };

  const withdraw = async () => {
    const val = parseInt(withdrawAmount, 10);
    if (!val || val <= 0) return;
    setProcessing(true);
    try {
      const res = await fetch(`${API}/api/wallets/withdraw`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ amount_corcheas: val, currency: 'EUR' })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setMessage(lang === 'es'
          ? `Liquidacion solicitada: ${data.net_fiat}EUR (${data.net_corcheas} corcheas netas)`
          : `Cash out requested: ${data.net_fiat}EUR (${data.net_corcheas} corcheas net)`);
        setMessageType('success');
        setShowWithdraw(false);
        setWithdrawAmount('');
        fetchData();
      } else {
        setMessage(data.detail || 'Error');
        setMessageType('error');
      }
    } catch (e) {}
    setProcessing(false);
  };

  if (!user) return (
    <div className="min-h-screen pt-14 flex flex-col items-center justify-center gap-4">
      <Link to="/login" className="bg-pulse-primary text-black px-6 py-2 rounded-xl font-semibold">{t('auth.login')}</Link>
    </div>
  );

  if (loading) return <div className="min-h-screen pt-14 flex items-center justify-center"><div className="w-10 h-10 border-2 border-pulse-primary border-t-transparent rounded-full animate-spin" /></div>;

  const balance = wallet?.balance || 0;
  const balanceFiat = wallet?.balance_fiat || 0;

  const txIcon = (type) => {
    if (['purchase', 'welcome_bonus', 'ad_reward', 'publish_reward', 'donation_received', 'earn_subscription'].includes(type))
      return <ArrowDownLeft size={16} className="text-green-400" />;
    if (['payout', 'donation_sent'].includes(type))
      return <ArrowUpRight size={16} className="text-orange-400" />;
    if (['generate_base', 'generate_voice', 'generate_improve', 'spend_generation', 'spend_iteration'].includes(type))
      return <Music size={16} className="text-purple-400" />;
    return <RefreshCw size={16} className="text-pulse-primary" />;
  };

  const withdrawPreview = parseInt(withdrawAmount, 10) || 0;
  const withdrawFee = Math.floor(withdrawPreview * 0.3);
  const withdrawNet = withdrawPreview - withdrawFee;
  const withdrawNetFiat = (withdrawNet * 0.01).toFixed(2);

  return (
    <div data-testid="wallet-page" className="min-h-screen pt-14 pb-32">
      <div className="max-w-2xl mx-auto px-4 py-6">
        <Link to="/profile" className="flex items-center gap-2 text-pulse-muted hover:text-white mb-6 transition-colors"><ArrowLeft size={18} /> {t('common.back')}</Link>

        {message && (
          <div className={`mb-4 p-3 rounded-xl text-sm animate-fade-in-up flex items-center gap-2 ${
            messageType === 'success' ? 'bg-green-500/10 border border-green-500/20 text-green-400' :
            messageType === 'error' ? 'bg-red-500/10 border border-red-500/20 text-red-400' :
            messageType === 'warning' ? 'bg-yellow-500/10 border border-yellow-500/20 text-yellow-400' :
            'bg-pulse-primary/10 border border-pulse-primary/20 text-pulse-primary'
          }`}>
            {messageType === 'success' && <CheckCircle size={16} />}
            {messageType === 'error' && <XCircle size={16} />}
            {message}
            <button onClick={() => setMessage('')} className="ml-auto text-pulse-muted">x</button>
          </div>
        )}

        {pollingStatus === 'checking' && (
          <div className="mb-4 p-4 bg-pulse-surface border border-cyan-500/20 rounded-xl flex items-center gap-3 animate-pulse">
            <div className="w-6 h-6 border-2 border-pulse-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-sm">{lang === 'es' ? 'Verificando tu recarga...' : 'Verifying your top up...'}</p>
          </div>
        )}

        {/* Balance Card */}
        <div data-testid="wallet-balance" className="bg-gradient-to-br from-[#0d2847] to-[#0a1628] border border-cyan-500/20 rounded-2xl p-6 mb-6">
          <p className="text-xs text-pulse-muted uppercase tracking-wider mb-1">{lang === 'es' ? 'Tu Monedero' : 'Your Wallet'}</p>
          <div className="flex items-end gap-3 mb-1">
            <span data-testid="wallet-balance-corcheas" className="text-4xl font-heading font-extrabold text-white">{balance.toLocaleString()}</span>
            <span className="text-lg text-amber-400 font-bold mb-1">corcheas</span>
          </div>
          <p data-testid="wallet-balance-fiat" className="text-sm text-pulse-muted mb-4">
            {balanceFiat.toFixed(2)}EUR
            <span className="text-[10px] ml-1 opacity-60">(1EUR = 100 corcheas)</span>
          </p>
          <div className="flex gap-3">
            <button data-testid="deposit-btn" onClick={() => { setShowDeposit(true); setShowWithdraw(false); }} className="flex-1 bg-pulse-primary text-black py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:bg-pulse-primary-hover transition-colors">
              <Plus size={16} /> {lang === 'es' ? 'Recargar Corcheas' : 'Top Up Corcheas'}
            </button>
            <button data-testid="withdraw-btn" onClick={() => { setShowWithdraw(true); setShowDeposit(false); }} className="flex-1 bg-pulse-surface border border-pulse-border text-white py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 hover:border-cyan-500/30 transition-colors">
              <ArrowUpRight size={16} /> {lang === 'es' ? 'Liquidar' : 'Cash Out'}
            </button>
          </div>
        </div>

        {/* Deposit: Recargar Corcheas */}
        {showDeposit && (
          <div className="bg-pulse-surface border border-pulse-border rounded-xl p-5 mb-6 animate-fade-in-up">
            <h3 className="font-heading font-bold text-lg mb-2 flex items-center gap-2">
              <CreditCard size={18} className="text-pulse-primary" />
              {lang === 'es' ? 'Recargar Corcheas' : 'Top Up Corcheas'}
            </h3>
            <p className="text-xs text-pulse-muted mb-4">
              {lang === 'es' ? 'Selecciona un paquete. 1EUR = 100 corcheas.' : 'Select a package. 1EUR = 100 corcheas.'}
            </p>
            <div className="grid grid-cols-3 gap-2 mb-4">
              {depositPackages.map(pkg => (
                <button
                  key={pkg.id}
                  data-testid={`deposit-pkg-${pkg.id}`}
                  onClick={() => setSelectedPackage(pkg.id)}
                  className={`py-4 rounded-xl border transition-all flex flex-col items-center gap-1 ${
                    selectedPackage === pkg.id
                      ? 'bg-pulse-primary text-black border-pulse-primary scale-105'
                      : 'bg-pulse-elevated border-pulse-border hover:border-cyan-500/30'
                  } ${pkg.popular ? 'ring-1 ring-amber-400/50' : ''}`}
                >
                  <span className="text-lg font-bold">{pkg.amount_fiat}EUR</span>
                  <span className={`text-xs ${selectedPackage === pkg.id ? 'text-black/70' : 'text-amber-400'}`}>
                    {pkg.corcheas.toLocaleString()} corcheas
                  </span>
                  {pkg.popular && <span className="text-[9px] bg-amber-400/20 text-amber-400 px-1.5 py-0.5 rounded mt-0.5">Popular</span>}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <button
                data-testid="confirm-deposit"
                onClick={initiateStripeDeposit}
                disabled={processing || !selectedPackage}
                className="flex-1 bg-pulse-primary text-black py-3 rounded-xl font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {processing
                  ? <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                  : <CreditCard size={16} />}
                {lang === 'es' ? 'Recargar' : 'Top Up'}
              </button>
              <button onClick={() => { setShowDeposit(false); setSelectedPackage(null); }} className="px-6 py-3 text-pulse-muted text-sm">
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}

        {/* Withdraw */}
        {showWithdraw && (
          <div className="bg-pulse-surface border border-pulse-border rounded-xl p-5 mb-6 animate-fade-in-up">
            <h3 className="font-heading font-bold text-lg mb-2 flex items-center gap-2">
              <ArrowUpRight size={18} className="text-orange-400" />
              {lang === 'es' ? 'Liquidar Fondos' : 'Cash Out'}
            </h3>
            <p className="text-xs text-pulse-muted mb-4">
              {lang === 'es' ? 'Convierte corcheas a euros. Comision del 30%.' : 'Convert corcheas to euros. 30% commission.'}
            </p>
            <input
              data-testid="withdraw-amount"
              type="number"
              value={withdrawAmount}
              onChange={e => setWithdrawAmount(e.target.value)}
              placeholder={lang === 'es' ? 'Cantidad en corcheas' : 'Amount in corcheas'}
              className="w-full bg-pulse-elevated border border-pulse-border rounded-xl px-4 py-3 text-sm mb-2 focus:ring-2 focus:ring-cyan-500 focus:outline-none"
            />
            {withdrawPreview > 0 && (
              <div className="text-xs text-pulse-muted mb-4 space-y-1 bg-pulse-elevated/50 p-3 rounded-lg">
                <p>{lang === 'es' ? 'Liquidas' : 'Cashing out'}: <span className="text-white font-semibold">{withdrawPreview.toLocaleString()} corcheas</span></p>
                <p>{lang === 'es' ? 'Comision (30%)' : 'Fee (30%)'}: <span className="text-red-400">{withdrawFee.toLocaleString()} corcheas</span></p>
                <p className="text-green-400 font-semibold">
                  {lang === 'es' ? 'Recibiras' : 'You receive'}: {withdrawNet.toLocaleString()} corcheas = {withdrawNetFiat}EUR
                </p>
              </div>
            )}
            <div className="flex gap-2">
              <button data-testid="confirm-withdraw" onClick={withdraw} disabled={processing || !withdrawAmount || withdrawPreview <= 0} className="flex-1 bg-orange-500 text-black py-3 rounded-xl font-bold text-sm disabled:opacity-50">
                {processing ? '...' : (lang === 'es' ? 'Confirmar Liquidacion' : 'Confirm')}
              </button>
              <button onClick={() => setShowWithdraw(false)} className="px-6 py-3 text-pulse-muted text-sm">{t('common.cancel')}</button>
            </div>
          </div>
        )}

        {/* Transactions */}
        <h3 className="font-heading font-bold text-lg mb-4 flex items-center gap-2"><Clock size={18} /> {lang === 'es' ? 'Movimientos' : 'Transactions'}</h3>
        {transactions.length === 0 ? (
          <p className="text-pulse-muted text-sm text-center py-8">{lang === 'es' ? 'Sin movimientos aun' : 'No transactions yet'}</p>
        ) : (
          <div className="space-y-2">
            {transactions.map(tx => (
              <div key={tx.id} data-testid={`tx-${tx.id}`} className="flex items-center gap-3 p-3 bg-pulse-surface border border-pulse-border rounded-xl">
                <div className="w-9 h-9 rounded-lg bg-pulse-elevated flex items-center justify-center">{txIcon(tx.type)}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm truncate">{tx.description}</p>
                  <p className="text-[10px] text-pulse-muted">{new Date(tx.created_at).toLocaleDateString(lang === 'es' ? 'es-ES' : 'en-US')}</p>
                </div>
                <div className="text-right">
                  <p className={`text-sm font-bold ${tx.amount > 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {tx.amount > 0 ? '+' : ''}{tx.amount.toLocaleString()}
                  </p>
                  <span className="text-[9px] text-amber-400/80">corcheas</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
